<script setup lang="ts">
import { computed, toRefs } from "vue";
import { getInitials } from "@/components/MyStudents/Submissions/components/InternalComments/utils";
import ClockIcon from "@/components/shared/icons/ClockIcon.vue";
import QuestionAnswerRow from "./QuestionAnswerRow.vue";
import type { RecommenderResponseCardProps } from "./types";
import { formatCardDate } from "./utils";

const props = defineProps<RecommenderResponseCardProps>();
const { card } = toRefs(props);

const initials = computed<string>(() => getInitials(card.value.name));
const roleLine = computed<string>(() => [card.value.title, card.value.organization].filter(Boolean).join(" · "));
const contactLine = computed<string>(() => [card.value.email, card.value.phone].filter(Boolean).join(" · "));
const submittedOn = computed<string>(() => formatCardDate(card.value.submittedAt));
const requestedOn = computed<string>(() => formatCardDate(card.value.requestedAt));
</script>

<template>
  <div data-testid="recommender-response-card">
    <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
      <div class="flex flex-1 min-w-0 items-center gap-3 rounded-md bg-blue-400 p-4">
        <span
          class="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-full bg-teal-900 text-13p font-bold tracking-[0.3px] text-white"
          data-testid="recommender-response-avatar"
        >
          {{ initials }}
        </span>
        <div class="min-w-0">
          <div class="text-sm font-bold text-indigo-base" data-testid="recommender-response-name">
            {{ card.name }}
          </div>
          <div v-if="roleLine" class="text-xs text-gray-600" data-testid="recommender-response-role">
            {{ roleLine }}
          </div>
          <div class="text-xs text-teal-900 break-words" data-testid="recommender-response-contact">
            {{ contactLine }}
          </div>
        </div>
      </div>
      <div class="shrink-0 md:text-right">
        <div class="text-11p font-bold uppercase tracking-wider text-gray-800" data-testid="recommender-response-date-label">
          {{ card.isAwaiting ? "Requested" : "Date Submitted" }}
        </div>
        <div class="text-sm font-bold text-indigo-base" data-testid="recommender-response-date">
          {{ card.isAwaiting ? requestedOn : submittedOn }}
        </div>
      </div>
    </div>
    <div
      v-if="card.isAwaiting"
      class="mt-5 flex items-center gap-2 text-13p font-medium text-gray-800"
      data-testid="recommender-response-awaiting"
    >
      <ClockIcon class="w-4 h-4 shrink-0" />
      Awaiting Response
    </div>
    <div v-else-if="card.answers.length" class="mt-5 flex flex-col" data-testid="recommender-response-answers">
      <QuestionAnswerRow v-for="answer in card.answers" :key="answer.key" :answer="answer" />
    </div>
  </div>
</template>
